// Step 1: Install React Icons
// Run this command in your terminal:
// npm install react-icons
// or
// yarn add react-icons

// Step 2: Update your IntegrationsPage.jsx file
import React, { useState } from 'react';
import PageTransition from '../components/ui/PageTransition';
import { motion } from 'framer-motion';
import IntegrationCard from '../components/ui/IntegrationCard';
import SignupFormModal from '../components/ui/SignupFormModal';
import Analytics from '../services/analytics';

// Import icons from react-icons
import { 
  SiSlack, 
  SiDiscord, 
  SiGmail, 
  SiWhatsapp, 
  SiTelegram,
  SiAmazon,
  SiGooglecloud, 
  SiSalesforce, 
  SiZendesk, 
  SiIntercom,
  SiGithub, 
  SiGitlab, 
  SiJira, 
  SiAsana, 
  SiTrello, 
  SiNotion,
  SiMailchimp, 
  SiHubspot, 
  SiQuickbooks, 
  SiStripe, 
  SiXero,
  SiZoom
} from "react-icons/si";

// Import icons from react-icons (different packs)
import { FaMicrosoft } from "react-icons/fa";

const categories = [
  'All',
  'Communication',
  'Project Management',
  'Customer Support',
  'Developer Tools',
  'Cloud Services',
  'Marketing',
  'Finance',
];

const integrations = [
  {
    name: 'Slack',
    description: 'Deploy AI agents in channels and DMs to answer questions and automate tasks.',
    icon: SiSlack({ size: 36 }),
    color: 'bg-blue-100',
    category: 'Communication',
  },
  {
    name: 'Discord',
    description: 'Add AI agents to your Discord server for community management and support.',
    icon: SiDiscord({ size: 36 }),
    color: 'bg-purple-100',
    category: 'Communication',
  },
  {
    name: 'Microsoft Teams',
    description: 'Integrate AI-powered assistants directly into your Microsoft Teams workspace.',
    icon: FaMicrosoft({ size: 36 }),
    color: 'bg-indigo-100',
    category: 'Communication',
  },
  {
    name: 'Gmail',
    description: 'Automate email responses and organization with intelligent AI assistance.',
    icon: SiGmail({ size: 36 }),
    color: 'bg-red-100',
    category: 'Communication',
  },
  {
    name: 'WhatsApp',
    description: 'Create AI agents that communicate via WhatsApp for customer support.',
    icon: SiWhatsapp({ size: 36 }),
    color: 'bg-green-100',
    category: 'Communication',
  },
  {
    name: 'Telegram',
    description: 'Build AI bots for Telegram that engage with users and automate workflows.',
    icon: SiTelegram({ size: 36 }),
    color: 'bg-blue-100',
    category: 'Communication',
  },
  {
    name: 'AWS',
    description: 'Connect your AWS services for automated infrastructure management.',
    icon: SiAmazon({ size: 36 }),
    color: 'bg-orange-100',
    category: 'Cloud Services',
  },
  {
    name: 'Google Cloud',
    description: 'Automate operations and management of your Google Cloud resources.',
    icon: SiGooglecloud({ size: 36 }),
    color: 'bg-blue-100',
    category: 'Cloud Services',
  },
  {
    name: 'Azure',
    description: 'Integrate AI agents with your Microsoft Azure cloud services.',
    icon: FaMicrosoft({ size: 36 }),
    color: 'bg-indigo-100',
    category: 'Cloud Services',
  },
  {
    name: 'Salesforce',
    description: 'Integrate AI agents with your CRM for intelligent customer insights.',
    icon: SiSalesforce({ size: 36 }),
    color: 'bg-blue-100',
    category: 'Customer Support',
  },
  {
    name: 'Zendesk',
    description: 'AI-powered support agents that integrate directly with your helpdesk.',
    icon: SiZendesk({ size: 36 }),
    color: 'bg-green-100',
    category: 'Customer Support',
  },
  {
    name: 'Intercom',
    description: 'Enhance your customer messaging with AI-powered conversations.',
    icon: SiIntercom({ size: 36 }),
    color: 'bg-blue-100',
    category: 'Customer Support',
  },
  {
    name: 'GitHub',
    description: 'Automate code reviews, issue management, and development workflows.',
    icon: SiGithub({ size: 36 }),
    color: 'bg-gray-100',
    category: 'Developer Tools',
  },
  {
    name: 'GitLab',
    description: 'Streamline your development process with intelligent CI/CD automation.',
    icon: SiGitlab({ size: 36 }),
    color: 'bg-orange-100',
    category: 'Developer Tools',
  },
  {
    name: 'Jira',
    description: 'AI-assisted project management and ticket handling for your team.',
    icon: SiJira({ size: 36 }),
    color: 'bg-blue-100',
    category: 'Project Management',
  },
  {
    name: 'Asana',
    description: 'Automate task creation, assignment, and tracking in your projects.',
    icon: SiAsana({ size: 36 }),
    color: 'bg-red-100',
    category: 'Project Management',
  },
  {
    name: 'Trello',
    description: 'Add AI-powered automation to your Trello boards and workflows.',
    icon: SiTrello({ size: 36 }),
    color: 'bg-blue-100',
    category: 'Project Management',
  },
  {
    name: 'Notion',
    description: 'Intelligent agents that help organize and retrieve information from your workspace.',
    icon: SiNotion({ size: 36 }),
    color: 'bg-gray-100',
    category: 'Project Management',
  },
  {
    name: 'Mailchimp',
    description: 'Optimize your email marketing campaigns with AI-driven insights.',
    icon: SiMailchimp({ size: 36 }),
    color: 'bg-yellow-100',
    category: 'Marketing',
  },
  {
    name: 'HubSpot',
    description: 'Enhance your marketing, sales, and service with intelligent automation.',
    icon: SiHubspot({ size: 36 }),
    color: 'bg-orange-100',
    category: 'Marketing',
  },
  {
    name: 'QuickBooks',
    description: 'Automate financial processes and get AI-powered financial insights.',
    icon: SiQuickbooks({ size: 36 }),
    color: 'bg-green-100',
    category: 'Finance',
  },
  {
    name: 'Stripe',
    description: 'Intelligent payment processing and fraud detection with AI.',
    icon: SiStripe({ size: 36 }),
    color: 'bg-purple-100',
    category: 'Finance',
  },
  {
    name: 'Xero',
    description: 'Streamline your accounting with AI-powered automation and insights.',
    icon: SiXero({ size: 36 }),
    color: 'bg-blue-100',
    category: 'Finance',
  },
  {
    name: 'Zoom',
    description: 'Add AI assistants to your video meetings for notes and action items.',
    icon: SiZoom({ size: 36 }),
    color: 'bg-blue-100',
    category: 'Communication',
  },
];

const IntegrationsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState('');
  
  const filteredIntegrations = integrations.filter(integration => {
    return (
      (selectedCategory === 'All' || integration.category === selectedCategory) &&
      integration.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleLearnMore = (integrationName: string) => {
    setSelectedIntegration(integrationName);
    Analytics.trackButtonClick('Learn More - ' + integrationName, 'integrations-page');
    // Scroll to the top of the page before showing the modal
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsSignupModalOpen(true);
  };

  const handleRequestIntegration = () => {
    Analytics.trackButtonClick('Request Integration', 'integrations-page');
    // Scroll to the top of the page before showing the modal
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsSignupModalOpen(true);
  };

  return (
    <>
      <PageTransition>
        <section className="pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <motion.span 
                className="text-primary-600 font-semibold tracking-wide uppercase text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                All Integrations
              </motion.span>
              <motion.h1 
                className="mt-2 text-4xl sm:text-5xl font-bold text-gray-900"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                Connect with Your Favorite Tools
              </motion.h1>
              <motion.p 
                className="mt-4 text-xl text-gray-600"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                AgentSpot integrates with over 50+ popular platforms and services to automate your workflow.
              </motion.p>
            </div>
            
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="relative max-w-md mx-auto">
                <input
                  type="text"
                  placeholder="Search integrations..."
                  className="w-full px-4 py-3 pl-10 pr-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg 
                  className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
            </motion.div>
            
            <motion.div 
              className="flex flex-wrap justify-center gap-2 mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {categories.map((category, index) => (
                <motion.button
                  key={index}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => setSelectedCategory(category)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category}
                </motion.button>
              ))}
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredIntegrations.map((integration, index) => (
                <IntegrationCard
                  key={index}
                  name={integration.name}
                  description={integration.description}
                  icon={integration.icon}
                  color={integration.color}
                  delay={index * 0.05}
                  onLearnMore={handleLearnMore}
                />
              ))}
            </div>
            
            {filteredIntegrations.length === 0 && (
              <div className="text-center py-16">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No integrations found</h3>
                <p className="mt-2 text-gray-500">Try adjusting your search or filter to find what you're looking for.</p>
              </div>
            )}
            
            <motion.div
              className="mt-16 p-8 bg-gray-100 rounded-xl text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold mb-4">Can't find what you need?</h3>
              <p className="text-gray-600 mb-6">
                If you don't see the integration you're looking for, let us know. We're constantly adding new integrations based on user requests.
              </p>
              <motion.button
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleRequestIntegration}
              >
                Request an Integration
              </motion.button>
            </motion.div>
          </div>
        </section>
      </PageTransition>
      
      {/* Signup Modal - Moved outside PageTransition */}
      <SignupFormModal 
        isOpen={isSignupModalOpen} 
        onClose={() => setIsSignupModalOpen(false)} 
      />
    </>
  );
};

export default IntegrationsPage;